import i18n from "../i18n";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatCurrency, formatNumber } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Plus, Truck, Users, ClipboardList, Clock3, PackageOpen, BarChart3, ArrowRightLeft, Search, CircleDollarSign } from "lucide-react";
import { toast } from "sonner";
import { farmDateIso } from "@/lib/farmTime";

const localApi = api;

const ORDER_STATUS_OPTIONS = ["draft", "requested", "approved", "ordered", "partial", "received", "cancelled"];

function MetricCard({ label, value, sub, icon: Icon, tone = "default" }) {
    const tones = {
        default: "",
        good: "bg-primary/5 border-primary/20",
        warn: "bg-amber-500/5 border-amber-500/20",
        bad: "bg-destructive/5 border-destructive/20",
    };
    return (
        <Card className={`p-4 ${tones[tone]}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="tiny-label truncate">{label}</div>
                    <div className="mt-2 text-2xl font-bold font-mono truncate">{value}</div>
                    {sub ? <div className="mt-1 text-xs text-muted-foreground truncate">{sub}</div> : null}
                </div>
                {Icon ? <Icon className="w-5 h-5 text-primary shrink-0" /> : null}
            </div>
        </Card>
    );
}

function EmptyState({ title, description }) {
    return (
        <Card className="p-6 text-center text-sm text-muted-foreground">
            <div className="font-semibold text-foreground">{title}</div>
            <div className="mt-1">{description}</div>
        </Card>
    );
}

function toNumber(value) {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
}

function uid(prefix) {
    return `${prefix}-${farmDateIso().replace(/-/g, "")}-${crypto.randomUUID()}`;
}

function buildOrderDialog(order, fallbackItems) {
    const line = (order?.items || [])[0] || {};
    const selectedItem = fallbackItems.find((item) => item.id === line.item_id);
    return {
        id: order?.id || "",
        supplier: order?.supplier || "",
        item_id: line.item_id || selectedItem?.id || fallbackItems[0]?.id || "",
        item_name: line.item_name || selectedItem?.name || "",
        quantity: line.quantity ?? 0,
        received_quantity: line.received_quantity ?? 0,
        unit: line.unit || selectedItem?.unit || "unit",
        unit_cost: line.unit_cost ?? 0,
        order_date: order?.order_date || farmDateIso(),
        expected_date: order?.expected_date || "",
        status: order?.status || "draft",
        notes: order?.notes || "",
        source_submission_id: order?.source_submission_id || uid("purchase-order"),
    };
}

export default function PurchasingPage() {
    const { can } = useAuth();
    const canManageInventory = can("manage_inventory");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [farm, setFarm] = useState({ currency: "PHP" });
    const [items, setItems] = useState([]);
    const [orders, setOrders] = useState([]);
    const [query, setQuery] = useState("");
    const [tab, setTab] = useState("overview");
    const [orderDlg, setOrderDlg] = useState(null);
    const [receiveDlg, setReceiveDlg] = useState(null);
    const receiveSubmissionIds = useRef(new Map());

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const [farmResp, itemsResp, ordersResp] = await Promise.all([
                localApi.get("/farm").then((r) => r.data).catch(() => ({ currency: "PHP" })),
                localApi.get("/inventory-items").then((r) => r.data).catch(() => []),
                localApi.get("/purchase-orders").then((r) => r.data).catch(() => []),
            ]);
            setFarm(farmResp || { currency: "PHP" });
            setItems(Array.isArray(itemsResp) ? itemsResp : []);
            setOrders(Array.isArray(ordersResp) ? ordersResp : []);
        } catch (err) {
            setError(err.response?.data?.detail || err.message || "Failed to load purchasing data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const cur = (value) => formatCurrency(value || 0, farm.currency || "PHP");

    const suppliers = useMemo(() => {
        const map = new Map();
        items.forEach((item) => {
            const name = (item.supplier || "Unspecified").trim() || "Unspecified";
            const row = map.get(name) || { name, items: 0, value: 0, quantity: 0, source: "stock" };
            row.items += 1;
            row.quantity += toNumber(item.quantity);
            row.value += toNumber(item.inventory_value);
            map.set(name, row);
        });
        orders.forEach((order) => {
            const name = (order.supplier || "Unspecified").trim() || "Unspecified";
            const row = map.get(name) || { name, items: 0, value: 0, quantity: 0, source: "purchase_orders" };
            row.items += 1;
            row.value += toNumber(order.total);
            map.set(name, row);
        });
        return [...map.values()].sort((a, b) => b.value - a.value);
    }, [items, orders]);

    const filteredOrders = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return [...orders]
            .filter((order) => {
                if (!needle) return true;
                return [order.supplier, order.status, order.notes, order.order_date, JSON.stringify(order.items || [])].join(" ").toLowerCase().includes(needle);
            })
            .sort((a, b) => (b.order_date || b.created_at || "").localeCompare(a.order_date || a.created_at || ""));
    }, [orders, query]);

    const outstandingOrders = useMemo(() => filteredOrders.filter((order) => !["received", "cancelled"].includes(String(order.status || "draft").toLowerCase()) && (order.items || []).some((line) => toNumber(line.quantity) > toNumber(line.received_quantity))), [filteredOrders]);
    const receivedOrders = useMemo(() => filteredOrders.filter((order) => String(order.status || "").toLowerCase() === "received"), [filteredOrders]);
    const draftOrders = useMemo(() => filteredOrders.filter((order) => ["draft", "requested", "approved", "ordered", "partial"].includes(String(order.status || "draft").toLowerCase())), [filteredOrders]);
    const purchaseValue = useMemo(() => orders.reduce((sum, order) => sum + toNumber(order.total), 0), [orders]);
    const supplierPayments = useMemo(() => orders.reduce((sum, order) => sum + (Array.isArray(order.items) ? order.items.reduce((lineSum, line) => lineSum + toNumber(line.received_quantity) * toNumber(line.unit_cost), 0) : 0), 0), [orders]);

    const saveOrder = async () => {
        if (!orderDlg?.supplier?.trim()) return toast.error("Supplier is required");
        if (!orderDlg?.item_id) return toast.error("Select an inventory item");
        if (toNumber(orderDlg.quantity) <= 0) return toast.error("Quantity must be greater than 0");
        try {
            const item = items.find((entry) => entry.id === orderDlg.item_id);
            const body = {
                supplier: orderDlg.supplier.trim(),
                order_date: orderDlg.order_date || farmDateIso(),
                expected_date: orderDlg.expected_date || undefined,
                status: orderDlg.status || "draft",
                notes: orderDlg.notes || "",
                source_submission_id: orderDlg.source_submission_id,
                items: [{
                    item_id: orderDlg.item_id,
                    item_name: item?.name || orderDlg.item_name || "",
                    quantity: toNumber(orderDlg.quantity),
                    received_quantity: toNumber(orderDlg.received_quantity),
                    unit: orderDlg.unit || item?.unit || "unit",
                    unit_cost: toNumber(orderDlg.unit_cost),
                }],
            };
            if (orderDlg.id) await localApi.put(`/purchase-orders/${orderDlg.id}`, body); else await localApi.post("/purchase-orders", body);
            toast.success("Purchase order saved");
            setOrderDlg(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save purchase order");
        }
    };

    const receiveOrder = async (order) => {
        const replayKey = `${order.id}:${(order.items || []).map((line) => `${line.item_id}:${toNumber(line.received_quantity)}`).join("|")}`;
        if (!receiveSubmissionIds.current.has(replayKey)) {
            receiveSubmissionIds.current.set(replayKey, uid(`po-${order.id}`));
        }
        try {
            await localApi.post(`/purchase-orders/${order.id}/receive`, {
                received_date: farmDateIso(),
                source_submission_id: receiveSubmissionIds.current.get(replayKey),
                items: (order.items || []).map((line) => ({
                    item_id: line.item_id,
                    quantity: Math.max(0, toNumber(line.quantity) - toNumber(line.received_quantity)),
                })),
            });
            toast.success("Purchase order received");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to receive purchase order");
        }
    };

    if (loading) return <div className="p-8 text-center text-muted-foreground">{i18n.t("final.616ec7943070")}</div>;
    if (error) {
        return (
            <Card className="p-6 border-destructive/40 bg-destructive/5" data-testid="purchasing-error">
                <div className="font-semibold text-destructive">{i18n.t("final.bacca935870a")}</div>
                <div className="text-sm text-muted-foreground mt-1">{error}</div>
                <Button className="mt-4" variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-2" />{i18n.t("final.9f5cd8a2e880")}</Button>
            </Card>
        );
    }

    return (
        <div className="space-y-6" data-testid="purchasing-page">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <div className="tiny-label">{i18n.t("final.ae65324b857d")}</div>
                    <h1 className="text-4xl font-bold tracking-tight mt-2">{i18n.t("final.6e3d52434a58")}</h1>
                    <p className="text-muted-foreground mt-2">{i18n.t("final.92210c2cbd8f")}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-2" />{i18n.t("final.56e3badc4e6c")}</Button>
                    <Dialog open={!!orderDlg} onOpenChange={(open) => !open && setOrderDlg(null)}>
                        <DialogTrigger asChild>
                            <Button onClick={() => setOrderDlg(buildOrderDialog(null, items))} disabled={!canManageInventory} data-testid="add-purchase-order-btn">
                                <Plus className="w-4 h-4 mr-2" />{i18n.t("final.c59c94e09696")}
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl">
                            <DialogHeader><DialogTitle>{orderDlg?.id ? "Edit" : "New"} {i18n.t("final.3c45b957fdc8")}</DialogTitle></DialogHeader>
                            {orderDlg ? (
                                <div className="space-y-4">
                                    <div className="grid gap-3 md:grid-cols-2">
                                        <div><Label>{i18n.t("final.396c65e60e58")}</Label><Input value={orderDlg.supplier} onChange={(e) => setOrderDlg({ ...orderDlg, supplier: e.target.value })} className="h-11 mt-1" data-testid="po-supplier-input" /></div>
                                        <div><Label>{i18n.t("final.2637a450046c")}</Label>
                                            <Select value={orderDlg.item_id} onValueChange={(value) => {
                                                const selected = items.find((item) => item.id === value);
                                                setOrderDlg({ ...orderDlg, item_id: value, unit: selected?.unit || orderDlg.unit || "unit" });
                                            }}>
                                                <SelectTrigger className="h-11 mt-1"><SelectValue placeholder={i18n.t("final.b0946c471bef")} /></SelectTrigger>
                                                <SelectContent>{items.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div><Label>{i18n.t("final.44f6af694554")}</Label><Input type="number" min="1" value={orderDlg.quantity} onChange={(e) => setOrderDlg({ ...orderDlg, quantity: e.target.value })} className="h-11 mt-1" /></div>
                                        <div><Label>{i18n.t("final.f6b935ab3371")}</Label><Input value={orderDlg.unit} onChange={(e) => setOrderDlg({ ...orderDlg, unit: e.target.value })} className="h-11 mt-1" /></div>
                                        <div><Label>{i18n.t("final.0105252023c5")}</Label><Input type="number" min="0" step="0.01" value={orderDlg.unit_cost} onChange={(e) => setOrderDlg({ ...orderDlg, unit_cost: e.target.value })} className="h-11 mt-1" /></div>
                                        <div><Label>{i18n.t("final.bae7d5be7082")}</Label>
                                            <Select value={orderDlg.status} onValueChange={(value) => setOrderDlg({ ...orderDlg, status: value })}>
                                                <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                                                <SelectContent>{ORDER_STATUS_OPTIONS.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div><Label>{i18n.t("final.439e231109dc")}</Label><Input type="date" value={orderDlg.order_date} onChange={(e) => setOrderDlg({ ...orderDlg, order_date: e.target.value })} className="h-11 mt-1" /></div>
                                        <div><Label>{i18n.t("final.84d296628caf")}</Label><Input type="date" value={orderDlg.expected_date} onChange={(e) => setOrderDlg({ ...orderDlg, expected_date: e.target.value })} className="h-11 mt-1" /></div>
                                    </div>
                                    <div><Label>{i18n.t("final.70440046a3dc")}</Label><Input value={orderDlg.notes} onChange={(e) => setOrderDlg({ ...orderDlg, notes: e.target.value })} className="h-11 mt-1" /></div>
                                    <Button onClick={saveOrder} className="w-full h-11" data-testid="save-purchase-order-btn">{i18n.t("final.d64f648a7f85")}</Button>
                                </div>
                            ) : null}
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3" data-testid="purchasing-kpis">
                <MetricCard label={i18n.t("final.4dcd818cab26")} value={formatNumber(suppliers.length)} sub="live supplier sources" icon={Users} />
                <MetricCard label={i18n.t("final.1ea7ee5d6e95")} value={formatNumber(orders.length)} sub="all recorded orders" icon={ClipboardList} />
                <MetricCard label={i18n.t("final.f8ee57ec8645")} value={formatNumber(outstandingOrders.length)} sub="not fully received" icon={Clock3} tone={outstandingOrders.length > 0 ? "warn" : "default"} />
                <MetricCard label={i18n.t("final.fdd37fc793a8")} value={cur(purchaseValue)} sub="purchase total" icon={CircleDollarSign} />
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="h-auto flex flex-wrap gap-1" data-testid="purchasing-tabs">
                    <TabsTrigger value="overview">{i18n.t("final.0efc2e6be4c2")}</TabsTrigger>
                    <TabsTrigger value="suppliers">{i18n.t("final.4dcd818cab26")}</TabsTrigger>
                    <TabsTrigger value="requests">{i18n.t("final.6fceba68eac8")}</TabsTrigger>
                    <TabsTrigger value="orders">{i18n.t("final.1ea7ee5d6e95")}</TabsTrigger>
                    <TabsTrigger value="receiving">{i18n.t("final.2f8f376ad77b")}</TabsTrigger>
                    <TabsTrigger value="history">{i18n.t("final.1bd2d1a15a43")}</TabsTrigger>
                    <TabsTrigger value="analytics">{i18n.t("final.3d0ec3f6c93a")}</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-4">
                    <Card className="p-4">
                        <div className="grid gap-3 md:grid-cols-3">
                            <div><Label>{i18n.t("final.7ab3015465a9")}</Label><Input value={query} onChange={(e) => setQuery(e.target.value)} className="h-11 mt-1" placeholder={i18n.t("final.f7d8733d94d5")} data-testid="po-search-input" /></div>
                            <div><Label>{i18n.t("final.29706cca17e6")}</Label><div className="h-11 mt-1 rounded-md border border-border px-3 flex items-center font-semibold">{formatNumber(outstandingOrders.length)}</div></div>
                            <div><Label>{i18n.t("final.ac2dfb38024b")}</Label><div className="h-11 mt-1 rounded-md border border-border px-3 flex items-center font-semibold">{formatNumber(receivedOrders.length)}</div></div>
                        </div>
                    </Card>
                    <div className="grid gap-4 lg:grid-cols-2">
                        <Card className="p-4">
                            <div className="font-semibold mb-3">{i18n.t("final.877d5d5fa796")}</div>
                            {outstandingOrders.length === 0 ? <EmptyState title={i18n.t("final.dc3b2aa7a0e8")} description={i18n.t("final.9bd9a1fc0241")} /> : (
                                <Table>
                                    <TableHeader><TableRow><TableHead>{i18n.t("final.55edd462872a")}</TableHead><TableHead>{i18n.t("final.bae7d5be7082")}</TableHead><TableHead className="text-right">{i18n.t("final.b25928c69902")}</TableHead><TableHead /></TableRow></TableHeader>
                                    <TableBody>
                                        {outstandingOrders.slice(0, 6).map((order) => (
                                            <TableRow key={order.id}>
                                                <TableCell className="font-semibold">{order.supplier}</TableCell>
                                                <TableCell><Badge variant="outline">{order.status || "draft"}</Badge></TableCell>
                                                <TableCell className="text-right font-mono">{cur(order.total)}</TableCell>
                                                <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => receiveOrder(order)}>{i18n.t("final.965fb2ddf63d")}</Button></TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Card>
                        <Card className="p-4">
                            <div className="font-semibold mb-3">{i18n.t("final.0a82c3251c51")}</div>
                            {suppliers.length === 0 ? <EmptyState title={i18n.t("final.e3970bebf8df")} description={i18n.t("final.9d94dbbd57c9")} /> : (
                                <div className="space-y-3">
                                    {suppliers.slice(0, 5).map((supplier) => (
                                        <div key={supplier.name} className="rounded-lg border p-3 flex items-center justify-between gap-3">
                                            <div>
                                                <div className="font-semibold">{supplier.name}</div>
                                                <div className="text-xs text-muted-foreground">{formatNumber(supplier.items)} {i18n.t("final.86761b63a7bd")}</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-semibold">{cur(supplier.value)}</div>
                                                <div className="text-xs text-muted-foreground">{formatNumber(supplier.quantity)} {i18n.t("final.917625318331")}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </div>
                </TabsContent>

                <TabsContent value="suppliers" className="space-y-4">
                    <Card className="p-4">
                        <div className="font-semibold mb-3">{i18n.t("final.4dcd818cab26")}</div>
                        {suppliers.length === 0 ? <EmptyState title={i18n.t("final.f967a88e008c")} description={i18n.t("final.704aac3a9bc5")} /> : (
                            <Table>
                                <TableHeader><TableRow><TableHead>{i18n.t("final.709a23220f2c")}</TableHead><TableHead className="text-right">{i18n.t("final.e51c55255be9")}</TableHead><TableHead className="text-right">{i18n.t("final.44f6af694554")}</TableHead><TableHead className="text-right">{i18n.t("final.8dce170de238")}</TableHead><TableHead>{i18n.t("final.6da13addb000")}</TableHead></TableRow></TableHeader>
                                <TableBody>
                                    {suppliers.map((supplier) => (
                                        <TableRow key={supplier.name}>
                                            <TableCell className="font-semibold">{supplier.name}</TableCell>
                                            <TableCell className="text-right font-mono">{formatNumber(supplier.items)}</TableCell>
                                            <TableCell className="text-right font-mono">{formatNumber(supplier.quantity)}</TableCell>
                                            <TableCell className="text-right font-mono">{cur(supplier.value)}</TableCell>
                                            <TableCell>{supplier.source}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="requests" className="space-y-4">
                    <Card className="p-4">
                        <div className="font-semibold mb-3">{i18n.t("final.6fceba68eac8")}</div>
                        <div className="text-sm text-muted-foreground mb-4">{i18n.t("final.97ca5f493a3d")}</div>
                        {draftOrders.length === 0 ? <EmptyState title={i18n.t("final.c98cdd2ee123")} description={i18n.t("final.2b7b55a616a9")} /> : (
                            <Table>
                                <TableHeader><TableRow><TableHead>{i18n.t("final.55edd462872a")}</TableHead><TableHead>{i18n.t("final.bae7d5be7082")}</TableHead><TableHead>{i18n.t("final.439e231109dc")}</TableHead><TableHead className="text-right">{i18n.t("final.b25928c69902")}</TableHead><TableHead /></TableRow></TableHeader>
                                <TableBody>
                                    {draftOrders.map((order) => (
                                        <TableRow key={order.id}>
                                            <TableCell className="font-semibold">{order.supplier}</TableCell>
                                            <TableCell><Badge variant="outline">{order.status || "draft"}</Badge></TableCell>
                                            <TableCell>{order.order_date || "—"}</TableCell>
                                            <TableCell className="text-right font-mono">{cur(order.total)}</TableCell>
                                            <TableCell className="text-right space-x-2">
                                                <Button size="sm" variant="outline" onClick={() => setOrderDlg(buildOrderDialog(order, items))} disabled={!canManageInventory}>{i18n.t("final.5301648dcf6b")}</Button>
                                                <Button size="sm" variant="outline" onClick={() => receiveOrder(order)} disabled={!canManageInventory}>{i18n.t("final.965fb2ddf63d")}</Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="orders" className="space-y-4">
                    <Card className="p-4">
                        <div className="font-semibold mb-3">{i18n.t("final.1ea7ee5d6e95")}</div>
                        {filteredOrders.length === 0 ? <EmptyState title={i18n.t("final.35683fd19bd3")} description={i18n.t("final.89c4cc389c22")} /> : (
                            <Table>
                                <TableHeader><TableRow><TableHead>{i18n.t("final.55edd462872a")}</TableHead><TableHead>{i18n.t("final.bae7d5be7082")}</TableHead><TableHead>{i18n.t("final.44d25b5d1b6d")}</TableHead><TableHead className="text-right">{i18n.t("final.b25928c69902")}</TableHead><TableHead>{i18n.t("final.842b7b5d7189")}</TableHead><TableHead /></TableRow></TableHeader>
                                <TableBody>
                                    {filteredOrders.map((order) => (
                                        <TableRow key={order.id}>
                                            <TableCell className="font-semibold">{order.supplier}</TableCell>
                                            <TableCell><Badge variant="outline">{order.status || "draft"}</Badge></TableCell>
                                            <TableCell>{formatNumber((order.items || []).length)}</TableCell>
                                            <TableCell className="text-right font-mono">{cur(order.total)}</TableCell>
                                            <TableCell className="text-xs">{order.order_date || "—"}<div className="text-muted-foreground">{order.expected_date || "—"}</div></TableCell>
                                            <TableCell className="text-right space-x-2">
                                                <Button size="sm" variant="outline" onClick={() => setOrderDlg(buildOrderDialog(order, items))} disabled={!canManageInventory}>{i18n.t("final.5301648dcf6b")}</Button>
                                                <Button size="sm" variant="outline" onClick={() => receiveOrder(order)} disabled={!canManageInventory || String(order.status || "").toLowerCase() === "received"}>{i18n.t("final.965fb2ddf63d")}</Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="receiving" className="space-y-4">
                    <Card className="p-4">
                        <div className="font-semibold mb-3">{i18n.t("final.2f8f376ad77b")}</div>
                        {outstandingOrders.length === 0 ? <EmptyState title={i18n.t("final.4bf41b254218")} description={i18n.t("final.f729524fe595")} /> : (
                            <div className="space-y-3">
                                {outstandingOrders.map((order) => (
                                    <Card key={order.id} className="p-4">
                                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                            <div>
                                                <div className="font-semibold">{order.supplier}</div>
                                                <div className="text-sm text-muted-foreground">{order.items?.[0]?.item_name || "Purchase order"} · {order.status || "draft"}</div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="text-sm text-muted-foreground">{cur(order.total)}</div>
                                                <Button size="sm" onClick={() => receiveOrder(order)} disabled={!canManageInventory}>{i18n.t("final.bca6293c399c")}</Button>
                                            </div>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="history" className="space-y-4">
                    <Card className="p-4">
                        <div className="font-semibold mb-3">{i18n.t("final.1bd2d1a15a43")}</div>
                        {receivedOrders.length === 0 ? <EmptyState title={i18n.t("final.7790f856aec6")} description={i18n.t("final.6553548c02a1")} /> : (
                            <Table>
                                <TableHeader><TableRow><TableHead>{i18n.t("final.55edd462872a")}</TableHead><TableHead>{i18n.t("final.439e231109dc")}</TableHead><TableHead>{i18n.t("final.27548c4fc95d")}</TableHead><TableHead className="text-right">{i18n.t("final.b25928c69902")}</TableHead><TableHead /></TableRow></TableHeader>
                                <TableBody>
                                    {receivedOrders.map((order) => (
                                        <TableRow key={order.id}>
                                            <TableCell className="font-semibold">{order.supplier}</TableCell>
                                            <TableCell>{order.order_date || "—"}</TableCell>
                                            <TableCell>{Array.isArray(order.receivings) && order.receivings.length > 0 ? "Yes" : "Yes"}</TableCell>
                                            <TableCell className="text-right font-mono">{cur(order.total)}</TableCell>
                                            <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => setOrderDlg(buildOrderDialog(order, items))} disabled={!canManageInventory}>{i18n.t("final.69bd4ef9fbd0")}</Button></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="analytics" className="space-y-4">
                    <div className="grid gap-4 lg:grid-cols-2">
                        <Card className="p-4">
                            <div className="font-semibold mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4" />{i18n.t("final.3d0ec3f6c93a")}</div>
                            <div className="space-y-3 text-sm">
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("final.25351761708f")}</span><span className="font-semibold">{cur(purchaseValue)}</span></div>
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("final.5a32ec8d373c")}</span><span className="font-semibold">{cur(outstandingOrders.reduce((sum, order) => sum + toNumber(order.total), 0))}</span></div>
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("final.4052cedd127f")}</span><span className="font-semibold">{formatNumber(suppliers.length)}</span></div>
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("final.ac2dfb38024b")}</span><span className="font-semibold">{formatNumber(receivedOrders.length)}</span></div>
                            </div>
                        </Card>
                        <Card className="p-4">
                            <div className="font-semibold mb-3 flex items-center gap-2"><ArrowRightLeft className="w-4 h-4" />{i18n.t("final.57a923cebb64")}</div>
                            <div className="space-y-3 text-sm">
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("final.e7048b02e423")}</span><span className="font-semibold">{cur(supplierPayments)}</span></div>
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("final.0c1e1d66e049")}</span><span className="font-semibold">{cur(Math.max(0, purchaseValue - supplierPayments))}</span></div>
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("final.a371e0e8950b")}</span><span className="font-semibold">{formatNumber(draftOrders.length)}</span></div>
                            </div>
                        </Card>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
